// src/components/vendor/VendorSalesHistory.tsx
import React, { useState } from 'react';
import {
    FileText,
    Download,
    Filter,
    Calendar,
    Package,
    CheckCircle,
    Clock,
    XCircle
} from 'lucide-react';
import './VendorTabs.css';

interface Sale {
    _id: string;
    productTitle: string;
    originalPriceCents: number;
    finalPriceCents: number;
    discountCents: number;
    vendorCommissionCents: number;
    couponCode?: string;
    paymentStatus: string;
    balanceStatus: 'pending' | 'available' | 'cancelled';
    daysSinceSale: number;
    createdAt: string;
}

interface VendorSalesHistoryProps {
    sales: Sale[];
    onRefresh: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

type FilterPeriod = 'all' | 'today' | 'week' | 'month';

const VendorSalesHistory: React.FC<VendorSalesHistoryProps> = ({ sales, onShowToast }) => {
    const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('all');
    const [showFilters, setShowFilters] = useState(false);

    const filterSalesByPeriod = (sales: Sale[], period: FilterPeriod): Sale[] => {
        if (period === 'all') return sales;

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        return sales.filter(sale => {
            const saleDate = new Date(sale.createdAt);

            switch (period) {
                case 'today':
                    return saleDate >= today;
                case 'week':
                    const weekAgo = new Date(today);
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return saleDate >= weekAgo;
                case 'month':
                    const monthAgo = new Date(today);
                    monthAgo.setMonth(monthAgo.getMonth() - 1);
                    return saleDate >= monthAgo;
                default:
                    return true;
            }
        });
    };

    const filteredSales = filterSalesByPeriod(sales, filterPeriod);

    const exportToCSV = () => {
        if (filteredSales.length === 0) {
            onShowToast('Nenhuma venda para exportar', 'info');
            return;
        }

        const headers = [
            'Data',
            'Produto',
            'Preço Original',
            'Desconto',
            'Valor Final',
            'Sua Comissão',
            'Cupom',
            'Status Pagamento',
            'Saldo Disponível'
        ];

        const rows = filteredSales.map(sale => [
            new Date(sale.createdAt).toLocaleDateString('pt-BR'),
            sale.productTitle,
            `R$ ${(sale.originalPriceCents / 100).toFixed(2)}`,
            sale.discountCents > 0 ? `R$ ${(sale.discountCents / 100).toFixed(2)}` : '-',
            `R$ ${(sale.finalPriceCents / 100).toFixed(2)}`,
            `R$ ${(sale.vendorCommissionCents / 100).toFixed(2)}`,
            sale.couponCode || '-',
            sale.paymentStatus === 'approved'
                ? 'Aprovado'
                : sale.paymentStatus === 'pending'
                    ? 'Pendente'
                    : 'Rejeitado',
            sale.balanceStatus === 'available'
                ? 'Sim'
                : sale.balanceStatus === 'cancelled'
                    ? 'Cancelado'
                    : 'Não'
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob(['\ufeff' + csvContent], {
            type: 'text/csv;charset=utf-8;'
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `vendas_${filterPeriod}_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();

        onShowToast('Relatório exportado!', 'success');
    };

    const getBalanceStatusBadge = (status: string, daysSince: number) => {
        switch (status) {
            case 'available':
                return (
                    <span className="vd-badge-available">
                        <CheckCircle size={14} /> Disponível
                    </span>
                );
            case 'cancelled':
                return (
                    <span className="vd-badge-cancelled">
                        <XCircle size={14} /> Cancelado
                    </span>
                );
            case 'pending':
                const daysRemaining = Math.max(0, 7 - daysSince);
                return (
                    <span className="vd-badge-pending">
                        <Clock size={14} /> {daysRemaining}d restantes
                    </span>
                );
            default:
                return <span className="vd-badge-pending">-</span>;
        }
    };

    const getPaymentStatusBadge = (status: string) => {
        switch (status) {
            case 'approved':
                return (
                    <span className="vd-status-badge vd-status-approved">
                        ✓ Aprovado
                    </span>
                );
            case 'pending':
                return (
                    <span className="vd-status-badge vd-status-pending">
                        ⏳ Pendente
                    </span>
                );
            case 'rejected':
                return (
                    <span className="vd-status-badge vd-status-rejected">
                        ✗ Rejeitado
                    </span>
                );
            case 'refunded':
                return (
                    <span className="vd-status-badge vd-status-cancelled">
                        ↩ Estornado
                    </span>
                );
            default:
                return <span className="vd-status-badge">{status}</span>;
        }
    };

    return (
        <div className="vendor-sales-history">
            <div className="vd-sales-history-header">
                <div>
                    <h2>Histórico de Vendas</h2>
                    <p className="vd-sales-subtitle">
                        Acompanhe todas as suas vendas e comissões
                    </p>
                </div>
                <div className="vd-sales-actions">
                    <button
                        className="vd-btn-filter"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <Filter size={18} />
                        Filtros
                    </button>
                    <button
                        className="vd-btn-export"
                        onClick={exportToCSV}
                    >
                        <Download size={18} />
                        Exportar CSV
                    </button>
                </div>
            </div>

            {showFilters && (
                <div className="vd-filters-panel">
                    <div className="vd-filter-group">
                        <label>Período:</label>
                        <div className="vd-filter-buttons">
                            <button
                                className={filterPeriod === 'all' ? 'vd-active' : ''}
                                onClick={() => setFilterPeriod('all')}
                            >
                                Tudo
                            </button>
                            <button
                                className={filterPeriod === 'today' ? 'vd-active' : ''}
                                onClick={() => setFilterPeriod('today')}
                            >
                                Hoje
                            </button>
                            <button
                                className={filterPeriod === 'week' ? 'vd-active' : ''}
                                onClick={() => setFilterPeriod('week')}
                            >
                                7 dias
                            </button>
                            <button
                                className={filterPeriod === 'month' ? 'vd-active' : ''}
                                onClick={() => setFilterPeriod('month')}
                            >
                                30 dias
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="vd-sales-summary">
                <div className="vd-summary-item">
                    <Calendar size={18} />
                    <span>
                        {filteredSales.length}{' '}
                        {filteredSales.length === 1 ? 'venda' : 'vendas'}
                    </span>
                </div>
                <div className="vd-summary-item">
                    <Package size={18} />
                    <span>
                        R${' '}
                        {(
                            filteredSales.reduce(
                                (sum, s) => sum + s.vendorCommissionCents,
                                0
                            ) / 100
                        ).toFixed(2)}{' '}
                        em comissões
                    </span>
                </div>
            </div>

            {filteredSales.length === 0 ? (
                <div className="vd-empty-state">
                    <FileText size={64} strokeWidth={1.5} />
                    <h3>Nenhuma venda encontrada</h3>
                    <p>Suas vendas aparecerão aqui</p>
                </div>
            ) : (
                <div className="vd-sales-table-container">
                    <table className="vd-sales-table">
                        <thead>
                            <tr>
                                <th>Data</th>
                                <th>Produto</th>
                                <th>Preço Original</th>
                                <th>Desconto</th>
                                <th>Valor Final</th>
                                <th>Sua Comissão</th>
                                <th>Cupom</th>
                                <th>Status</th>
                                <th>Saldo Disponível</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSales.map(sale => (
                                <tr key={sale._id}>
                                    <td>
                                        {new Date(sale.createdAt).toLocaleDateString(
                                            'pt-BR'
                                        )}
                                    </td>
                                    <td className="vd-product-cell">
                                        {sale.productTitle}
                                    </td>
                                    <td>
                                        R${' '}
                                        {(sale.originalPriceCents / 100).toFixed(2)}
                                    </td>
                                    <td>
                                        {sale.discountCents > 0 ? (
                                            <span className="vd-discount-value">
                                                - R${' '}
                                                {(sale.discountCents / 100).toFixed(2)}
                                            </span>
                                        ) : (
                                            <span className="vd-no-value">-</span>
                                        )}
                                    </td>
                                    <td className="vd-final-price">
                                        R${' '}
                                        {(sale.finalPriceCents / 100).toFixed(2)}
                                    </td>
                                    <td className="vd-commission-cell">
                                        R${' '}
                                        {(sale.vendorCommissionCents / 100).toFixed(2)}
                                    </td>
                                    <td>
                                        {sale.couponCode ? (
                                            <span className="vd-coupon-badge">
                                                {sale.couponCode}
                                            </span>
                                        ) : (
                                            <span className="vd-no-value">-</span>
                                        )}
                                    </td>
                                    <td>
                                        {getPaymentStatusBadge(sale.paymentStatus)}
                                    </td>
                                    <td>
                                        {getBalanceStatusBadge(
                                            sale.balanceStatus,
                                            sale.daysSinceSale
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="vd-info-box">
                <p>
                    <strong>💡 Sobre o período de segurança:</strong> As
                    comissões ficam disponíveis 7 dias após a aprovação do
                    pagamento para proteger contra estornos.
                </p>
            </div>
        </div>
    );
};

export default VendorSalesHistory;
